/**
 * «Тариф» (ТЗ §13).
 *
 * Оплата — Telegram Payments с провайдером Payme (`services/billingService.ts` на бэкенде):
 * кнопка просит у сервера ссылку на счёт и открывает её в Telegram. Пока на сервере нет
 * токена провайдера, `billing.enabled = false`: экран говорит «оплата подключается»,
 * а цены и кнопки нет вовсе — кнопка, не делающая ничего, хуже её отсутствия.
 *
 * Правила (шаг сетки, окно отмены, лимиты) сюда не попадают: пресет читается только ради
 * отрасли и срока триала.
 */

import { useCallback, useState } from 'react';
import { presetFor } from '@app/shared';
import { useAppApi } from '../../app/ApiProvider';
import { formatAmount, formatDateInTz } from '../../domain/datetime';
import { industryName } from '../../domain/catalog';
import { useAsync } from '../../hooks/useAsync';
import { useI18n } from '../../i18n/I18nProvider';
import { payInvoice } from '../../telegram/telegram';
import { CtaBar, CtaButton } from '../../ui/Cta';
import { Alert, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, SectionTitle, TopBar } from '../../ui/Screen';
import { useBusiness } from './useBusiness';
import { Card, ValueRow } from './profile/fields';

type PayOutcome = 'paid' | 'failed' | 'unsupported' | null;

export function PlanScreen(props: { businessId: string; onBack: () => void }): JSX.Element {
  const { t, lang } = useI18n();
  const api = useAppApi();
  const { state, business, reload } = useBusiness(props.businessId);
  const preset = business ? presetFor(business.type) : null;
  const billing = useAsync(
    useCallback(() => api.cabinet.getBilling(props.businessId), [api, props.businessId]),
    [props.businessId],
  );
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<PayOutcome>(null);
  const [payError, setPayError] = useState<unknown>(null);

  const tz = business?.tz ?? 'Asia/Tashkent';
  const info = billing.state.status === 'ready' ? billing.state.data : null;
  const active = info?.proUntil && new Date(info.proUntil) > new Date() ? info.proUntil : null;
  const trial = info?.trialEndsAt && new Date(info.trialEndsAt) > new Date() ? info.trialEndsAt : null;

  const pay = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setOutcome(null);
    setPayError(null);
    try {
      const { url } = await api.cabinet.createInvoice(props.businessId);
      const status = await payInvoice(url);
      if (status === 'paid') {
        setOutcome('paid');
        // Срок продлевает бот по `successful_payment` — он может прийти на миг позже статуса.
        setTimeout(billing.reload, 1500);
      } else if (status === 'failed') setOutcome('failed');
      else if (status === 'unsupported') setOutcome('unsupported');
    } catch (error) {
      setPayError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen>
      <TopBar title={t('planTitle')} subtitle={business?.name} onBack={props.onBack} />

      <ScrollArea bottomGap={info?.enabled ? 'both' : 'tabs'}>
        {state.status === 'loading' || billing.state.status === 'loading' ? <Loader /> : null}
        {state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} /> : null}
        {billing.state.status === 'failed' ? (
          <ErrorState error={billing.state.error} onRetry={billing.reload} />
        ) : null}

        {business && preset && info ? (
          <>
            {!info.enabled ? (
              <div className="px-4 pt-5">
                <Alert tone="warn" title={t('planTitle')}>
                  {t('planSoon')}
                </Alert>
              </div>
            ) : null}

            {outcome === 'paid' ? (
              <div className="px-4 pt-5">
                <Alert tone="plain" title={t('planPaid')}>
                  {t('planPaidNote')}
                </Alert>
              </div>
            ) : null}
            {outcome === 'failed' || payError ? (
              <div className="px-4 pt-5">
                <Alert tone="danger">{t('planPayFailed')}</Alert>
              </div>
            ) : null}
            {outcome === 'unsupported' ? (
              <div className="px-4 pt-5">
                <Alert tone="warn">{t('planPayOutside')}</Alert>
              </div>
            ) : null}

            <div className="px-4 pt-3">
              <SectionTitle>{t('planPriceTitle')}</SectionTitle>
              <Card>
                <ValueRow label={t('onbType')} value={industryName(preset, lang)} />
                <ValueRow
                  label={t('planStatus')}
                  value={
                    active
                      ? t('planProUntil', { date: formatDateInTz(active, tz, lang) })
                      : trial
                        ? t('planTrialUntil', { date: formatDateInTz(trial, tz, lang) })
                        : t('planFree')
                  }
                />
                {info.enabled && info.amountSum !== null ? (
                  <ValueRow
                    label={t(info.plan === 'solo' ? 'onbPriceSolo' : 'onbPriceMulti')}
                    value={`${formatAmount(info.amountSum, lang)} ${t('unitSumMonth')}`}
                  />
                ) : null}
              </Card>
            </div>
          </>
        ) : null}
      </ScrollArea>

      {info?.enabled ? (
        <CtaBar withTabs>
          <CtaButton onClick={() => void pay()} disabled={busy}>
            {t(active ? 'planExtend' : 'planPay')}
          </CtaButton>
        </CtaBar>
      ) : null}
    </Screen>
  );
}
